import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { MdLocalFireDepartment } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { productURL } from '../../utils/functions/common';
import { resolveHeroImage } from '../../utils/functions/heroImage';
import {
    categoryLabel,
    formatProductDuration,
    gridCardRating,
    heroCardTopChip,
} from '../../utils/functions/productCardMeta';
import { Product } from '../../utils/models/products';

const HERO_CARD_IMAGE_PX = 248;

const heroCardFont = {
    fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
} as const;

const topChipSx = {
    position: 'absolute',
    top: 12,
    left: 12,
    px: 1.25,
    py: 0.5,
    borderRadius: 10,
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    lineHeight: 1.2,
} as const;

export interface HeroFeaturedProductCardProps {
    readonly product: Product | undefined;
    readonly isStoreLoading: boolean;
    readonly isOnSale: (code: string) => number | null | undefined;
}

function HeroCardImage({ product, linkURL }: { readonly product: Product; readonly linkURL: string }) {
    const resolved = useMemo(() => resolveHeroImage(product), [product]);
    const alt = product.photos?.[0]?.alt || product.name;
    const fallbackSrc = product.photos?.[0]?.mediumURL;

    const inner =
        resolved?.webp != null ? (
            <picture style={{ display: 'block', height: '100%', margin: 0 }}>
                <source srcSet={resolved.webp} type="image/webp" />
                <Box
                    component="img"
                    src={resolved.imgSrc || fallbackSrc}
                    alt={alt}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'opacity 0.2s ease',
                        '&:hover': { opacity: 0.92 },
                    }}
                />
            </picture>
        ) : (
            <Box
                component="img"
                src={resolved?.imgSrc ?? fallbackSrc}
                alt={alt}
                sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'opacity 0.2s ease',
                    '&:hover': { opacity: 0.92 },
                }}
            />
        );

    return (
        <Box
            component={Link}
            to={linkURL}
            sx={{ display: 'block', outline: 'none', height: '100%' }}
            aria-label={`${product.name} — view details`}
        >
            {inner}
        </Box>
    );
}

function HeroFeaturedProductCard({ product, isStoreLoading, isOnSale }: HeroFeaturedProductCardProps) {
    const linkURL = product ? productURL(product) : '/';
    const salePrice = product ? isOnSale(product.productCode) : null;
    const topChip = product ? heroCardTopChip(product, salePrice) : null;
    const durationText = formatProductDuration(product);
    const location = product ? categoryLabel(product.category) : '';

    return (
        <Card
            data-hero-card
            elevation={0}
            sx={{
                ...heroCardFont,
                flex: '0 0 auto',
                alignSelf: 'stretch',
                width: { xs: 'calc(100vw - 72px)', sm: 360, md: 400 },
                scrollSnapAlign: 'start',
                overflow: 'hidden',
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.92)',
                border: '1px solid rgba(255,255,255,0.65)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                backdropFilter: 'blur(14px) saturate(1.2)',
                WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
                transition: 'opacity 0.5s ease',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    flexShrink: 0,
                    lineHeight: 0,
                    height: HERO_CARD_IMAGE_PX,
                }}
            >
                {isStoreLoading || !product ? (
                    <Skeleton
                        variant="rectangular"
                        sx={{ height: HERO_CARD_IMAGE_PX, transform: 'none', borderRadius: 0 }}
                    />
                ) : (
                    <HeroCardImage product={product} linkURL={linkURL} />
                )}
                {product && topChip ? (
                    <Box
                        component="span"
                        sx={{
                            ...topChipSx,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.45,
                            color: 'primary.contrastText',
                            bgcolor: 'primary.main',
                        }}
                    >
                        {topChip.kind === 'on_sale' ? (
                            <>
                                <LoyaltyIcon sx={{ fontSize: 16 }} />
                                On sale
                            </>
                        ) : (
                            <>
                                <MdLocalFireDepartment style={{ fontSize: 15, flexShrink: 0 }} />
                                {topChip.kind === 'popular'
                                    ? 'Popular'
                                    : topChip.kind === 'best_value'
                                      ? 'BEST VALUE'
                                      : topChip.text}
                            </>
                        )}
                    </Box>
                ) : null}
                {durationText ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.15,
                            py: 0.45,
                            borderRadius: 10,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'common.white',
                            backdropFilter: 'blur(6px)',
                        }}
                    >
                        <AccessTimeIcon sx={{ fontSize: 16 }} />
                        <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1 }}>
                            {durationText}
                        </Typography>
                    </Box>
                ) : null}
                {!isStoreLoading && product ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 10,
                            right: 10,
                            px: 1.25,
                            py: 0.85,
                            borderRadius: 1,
                            bgcolor: 'rgba(0,0,0,0.52)',
                            color: 'common.white',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.18)',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                color: 'rgba(255,255,255,0.8)',
                                textTransform: 'lowercase',
                                fontSize: '0.65rem',
                                mb: 0.15,
                                lineHeight: 1,
                            }}
                        >
                            From
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.25 }}>
                            {salePrice ? (
                                <>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '0.9rem',
                                            textDecoration: 'line-through',
                                            color: 'rgba(255,255,255,0.65)',
                                            fontWeight: 500,
                                            mr: 0.75,
                                        }}
                                    >
                                        ${product.displayPrice.price}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'common.white' }}
                                    >
                                        ${salePrice}
                                    </Typography>
                                </>
                            ) : (
                                <Typography
                                    component="span"
                                    sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'common.white' }}
                                >
                                    ${product.displayPrice.price}
                                </Typography>
                            )}
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.65)', ml: 0.2, fontSize: '0.7rem' }}
                            >
                                pp
                            </Typography>
                        </Box>
                    </Box>
                ) : null}
            </Box>

            <CardContent
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2.25,
                    pb: 2.25,
                    bgcolor: 'rgba(255,255,255,0.94)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    color: 'text.primary',
                    '&:last-child': { pb: 2.25 },
                }}
            >
                {isStoreLoading || !product ? (
                    <>
                        <Skeleton sx={{ bgcolor: 'rgba(0,0,0,0.08)', width: '60%', mb: 1.5 }} />
                        <Skeleton sx={{ bgcolor: 'rgba(0,0,0,0.08)', width: '90%' }} />
                        <Skeleton sx={{ bgcolor: 'rgba(0,0,0,0.08)', width: '70%', mt: 0.5 }} />
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                mb: 1.25,
                                typography: 'caption',
                                color: 'text.secondary',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                <StarRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ fontWeight: 600, color: 'text.primary' }}
                                >
                                    {gridCardRating(product.productCode)}
                                </Typography>
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    width: 3,
                                    height: 3,
                                    borderRadius: '50%',
                                    bgcolor: 'text.disabled',
                                    display: 'inline-block',
                                }}
                            />
                            <Typography component="span" variant="caption">
                                {location}
                            </Typography>
                        </Box>

                        <Typography
                            component={Link}
                            to={linkURL}
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: '1.15rem',
                                lineHeight: 1.3,
                                color: 'text.primary',
                                textDecoration: 'none',
                                mb: 1,
                                '&:hover': { color: 'primary.main' },
                            }}
                        >
                            {product.name}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.5,
                                fontSize: '0.875rem',
                                flex: 1,
                                mb: 1.5,
                                minHeight: 0,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {product.shortDescription}
                        </Typography>

                        <Typography
                            component={Link}
                            to={linkURL}
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.8125rem',
                                letterSpacing: '0.06em',
                                color: 'primary.main',
                                textDecoration: 'none',
                                mt: 'auto',
                                '&:hover': { color: 'primary.dark' },
                            }}
                        >
                            VIEW DETAILS →
                        </Typography>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

// Memoized: the card only depends on its (referentially-stable) product, the catalogue
// loading flag, and the stable isOnSale callback. This keeps unrelated store changes
// (e.g. availability fetches when a date is picked) from re-rendering the hero imagery.
export default React.memo(HeroFeaturedProductCard);
